import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  TrendingUp,
  TrendingDown,
  Users,
  Mail,
  DollarSign,
  Eye,
  LucideIcon,
} from "lucide-react";

interface MetricCardProps {
  title: string;
  value: number;
  previousValue?: number;
  format?: "number" | "currency" | "percent";
  icon: "users" | "mail" | "dollar" | "eye";
  sparklineData?: number[];
  delay?: number;
  isLoading?: boolean;
}

const iconMap: Record<string, LucideIcon> = {
  users: Users,
  mail: Mail,
  dollar: DollarSign,
  eye: Eye,
};

const iconColorMap: Record<string, string> = {
  users: "bg-blue-500/10 text-blue-500",
  mail: "bg-emerald-500/10 text-emerald-500",
  dollar: "bg-amber-500/10 text-amber-500",
  eye: "bg-violet-500/10 text-violet-500",
};

function AnimatedNumber({
  value,
  format,
}: {
  value: number;
  format: "number" | "currency" | "percent";
}) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => {
    if (format === "currency") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.round(latest));
    }
    if (format === "percent") {
      return `${latest.toFixed(1)}%`;
    }
    return new Intl.NumberFormat("en-US").format(Math.round(latest));
  });

  const displayRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const controls = animate(count, value, {
      duration: 1.5,
      ease: [0.25, 0.1, 0.25, 1],
    });
    return controls.stop;
  }, [count, value]);

  useEffect(() => {
    const unsubscribe = rounded.on("change", (v) => {
      if (displayRef.current) {
        displayRef.current.textContent = v;
      }
    });
    return unsubscribe;
  }, [rounded]);

  return <span ref={displayRef}>0</span>;
}

function SparklineChart({ data }: { data: number[] }) {
  const maxValue = Math.max(...data);
  const minValue = Math.min(...data);
  const range = maxValue - minValue || 1;

  const points = data
    .map((value, index) => {
      const x = (index / (data.length - 1)) * 100;
      const y = 100 - ((value - minValue) / range) * 100;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="w-full h-full"
    >
      <defs>
        <linearGradient id="sparklineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.5 }}
      />
      <motion.polygon
        points={`0,100 ${points} 100,100`}
        fill="url(#sparklineGradient)"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 1 }}
      />
    </svg>
  );
}

export function MetricCard({
  title,
  value,
  previousValue,
  format = "number",
  icon,
  sparklineData,
  delay = 0,
  isLoading = false,
}: MetricCardProps) {
  const Icon = iconMap[icon];
  const iconColors = iconColorMap[icon];

  const percentChange = previousValue
    ? ((value - previousValue) / previousValue) * 100
    : 0;
  const isPositive = percentChange >= 0;

  if (isLoading) {
    return (
      <Card className="p-6 relative overflow-hidden">
        <div className="flex items-start justify-between mb-4">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <Skeleton className="h-8 w-32 mb-2" />
        <Skeleton className="h-4 w-24" />
        <div className="absolute bottom-0 left-0 right-0 h-16 opacity-30">
          <Skeleton className="w-full h-full" />
        </div>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: delay * 0.1,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      <Card className="p-6 relative overflow-hidden group hover:shadow-lg transition-all duration-300 border-border/50 hover:border-border">
        {/* Subtle gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <div className="relative z-10">
          {/* Header with icon and trend */}
          <div className="flex items-start justify-between mb-4">
            <motion.div
              className={cn("p-2.5 rounded-xl", iconColors)}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <Icon className="w-5 h-5" />
            </motion.div>

            {previousValue !== undefined && (
              <motion.div
                className={cn(
                  "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                  isPositive
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : "bg-red-500/10 text-red-600 dark:text-red-400"
                )}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: delay * 0.1 + 0.3 }}
              >
                {isPositive ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                <span>{Math.abs(percentChange).toFixed(1)}%</span>
              </motion.div>
            )}
          </div>

          {/* Value */}
          <div className="mb-1">
            <span className="text-3xl font-semibold tracking-tight text-foreground">
              <AnimatedNumber value={value} format={format} />
            </span>
          </div>

          {/* Title */}
          <p className="text-sm text-muted-foreground font-medium">{title}</p>
        </div>

        {/* Sparkline */}
        {sparklineData && sparklineData.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-16 text-primary/40 group-hover:text-primary/60 transition-colors duration-300">
            <SparklineChart data={sparklineData} />
          </div>
        )}
      </Card>
    </motion.div>
  );
}

export function MetricCardSkeleton() {
  return (
    <Card className="p-6 relative overflow-hidden">
      <div className="flex items-start justify-between mb-4">
        <Skeleton className="h-10 w-10 rounded-xl" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <Skeleton className="h-8 w-32 mb-2" />
      <Skeleton className="h-4 w-24" />
      <div className="absolute bottom-0 left-0 right-0 h-16">
        <Skeleton className="w-full h-full rounded-none opacity-30" />
      </div>
    </Card>
  );
}
